import { type ClientIntent, type HandRecord, LIMITS, type ServerError, type TableSnapshot } from '@tctp/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type RoomAuth, type RoomConnection, type RoomStatus, connectRoom } from './socket';

export interface RoomError extends ServerError { id: number }

export interface Room {
  snapshot: TableSnapshot | null;
  /** The last completed hands, newest last: the `history` list from connect plus one record per hand that finished since. */
  history: HandRecord[];
  status: RoomStatus;
  refusal: string | null;
  errors: RoomError[];
  act(intent: ClientIntent): Promise<void>;
  dismiss(id: number): void;
}

let nextErrorId = 1;

/** Owns the socket for the page's lifetime; components render from `snapshot` and call `act`. `auth` must be referentially stable. */
export function useRoom(roomId: string, auth: RoomAuth, connect: typeof connectRoom = connectRoom): Room {
  const [snapshot, setSnapshot] = useState<TableSnapshot | null>(null);
  const [history, setHistory] = useState<HandRecord[]>([]);
  const [status, setStatus] = useState<RoomStatus>('connecting');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [errors, setErrors] = useState<RoomError[]>([]);
  const conn = useRef<RoomConnection | null>(null);
  const latest = useRef<TableSnapshot | null>(null);

  const pushError = useCallback((e: ServerError) => {
    const err: RoomError = { ...e, id: nextErrorId++ };
    setErrors(list => [...list, err]);
    setTimeout(() => setErrors(list => list.filter(x => x.id !== err.id)), 6_000);
  }, []);

  useEffect(() => {
    const c = connect(undefined, roomId, auth, {
      onSnapshot: s => { latest.current = s; setSnapshot(s); },
      onHistory: hands => setHistory(hands),
      onHandRecord: record => setHistory(list => (list.some(r => r.handId === record.handId) ? list : [...list, record].slice(-LIMITS.handHistory))),
      onError: e => { console.debug('serverError', e); },
      onStatus: (s, detail) => { setStatus(s); if (s === 'refused') setRefusal(detail ?? 'refused'); },
    });
    conn.current = c;
    return () => { c.close(); conn.current = null; };
  }, [roomId, auth, connect]);

  const act = useCallback(async (intent: ClientIntent) => {
    const sentAt = latest.current;
    try {
      await conn.current?.act(intent);
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === 'illegal_action' && latest.current !== sentAt) return;   // the table moved on before the action landed (a double tap, or the clock ran out): nothing to tell
      pushError({ code: err.code ?? 'error', message: err.message });
    }
  }, [pushError]);

  const dismiss = useCallback((id: number) => setErrors(list => list.filter(x => x.id !== id)), []);

  return { snapshot, history, status, refusal, errors, act, dismiss };
}
